import React, { useCallback, useEffect, useRef, useState } from "react";
import { Alert, Platform } from "react-native";
import {
  useIsConnected,
  useJoin,
  useLocalCameraTrack,
  useLocalMicrophoneTrack,
  usePublish,
  useRemoteUsers,
} from "../../../lib/agora";
import { getUserName, db, auth } from "../../../firebase";
import { collection, addDoc, serverTimestamp } from "firebase/firestore";
import { useNow } from "../../../hooks/useNow";
import { useVirtualBackground } from "../../../hooks/useVirtualBackground";
import { useNativeMediaPermissions } from "../../../hooks/useNativeMediaPermissions";
import { fetchRoomToken, GetRoomTokenResponse } from "../../FetchRoomToken";
import { useAppSelector } from "../../../redux/store";
import type { Room } from "../../../types/types";
import CallWebView from "../ui/CallWebView";
import { setCallHeaderLeaveHandler } from "../callHeaderActions";
import { logJoinRoom, markUserJoinedRoom } from "../../../utils/analyticsUtils";
import ReportUserModal from "../../../components/ReportUserModal";
import {
  heartbeatRoomPresence,
  joinRoomPresence,
  leaveRoomPresence,
} from "../../../services/roomPresenceService";

type Props = {
  room: Room;
  navigationReplace: (routeName: "MeetingsTabs" | "PostCall") => void;
};

/**
 * Web-only implementation using agora-rtc-react hooks/components.
 * Keeps parity with original behavior: token fetch, permission probe, participants count, names, timers, modals.
 */
export default function CallWebContainer({ room, navigationReplace }: Props) {
  const channelId = room.id;
  const roomName = room.name;

  // Report User Modal State
  const [isReportModalVisible, setIsReportModalVisible] = useState(false);
  const [flaggedUserId, setFlaggedUserId] = useState("");
  const [flaggedUserName, setFlaggedUserName] = useState("");
  const [isSubmittingReport, setIsSubmittingReport] = useState(false);

  const userName = useAppSelector((state) => state.user.name);
  useNow(); // keep the time ticking for auto-end and warning
  const currentTime = useAppSelector((state) => state.time.now);

  const [agoraCreds, setAgoraCreds] = useState<GetRoomTokenResponse | null>(
    null,
  );
  const [isFetchingToken, setIsFetchingToken] = useState(false);

  const [mediaPermissionError, setMediaPermissionError] = useState<
    string | null
  >(null);
  const [isCheckingPermissions, setIsCheckingPermissions] = useState(false);

  const [calling, setCalling] = useState(false);
  const isConnected = useIsConnected();
  const { status: nativePermissionStatus } = useNativeMediaPermissions();
  const hasJoinedPresenceRef = useRef(false);

  // Join with mic and camera off; the user turns them on (#142).
  const [micOn, setMicOn] = useState(false);
  const [cameraOn, setCameraOn] = useState(false);

  const { localMicrophoneTrack } = useLocalMicrophoneTrack(micOn);
  const { localCameraTrack } = useLocalCameraTrack(cameraOn);

  // Virtual background hook
  const { isBlurEnabled, toggleBlur } = useVirtualBackground(
    localCameraTrack,
    false,
  );

  // Publish tracks only while calling and when tracks exist
  usePublish(
    calling ? [localMicrophoneTrack, localCameraTrack].filter(Boolean) : [],
  );

  // Join/leave controlled by `calling`
  useJoin(
    {
      appid: agoraCreds?.appId ?? "",
      channel: agoraCreds?.channelId ?? "",
      token: (agoraCreds?.token as string | null) ?? null,
      // If you want to enforce a numeric UID, convert here and make sure your token function issues numeric-UID tokens:
      // uid: typeof agoraCreds?.rtcUid === "string" ? Number.parseInt(agoraCreds!.rtcUid as string, 10) : agoraCreds?.rtcUid,
      uid: agoraCreds?.rtcUid,
    },
    // only join once we have creds; on native also wait for granted permissions
    calling &&
      !!agoraCreds &&
      (Platform.OS === "web" || nativePermissionStatus === "granted"),
  );

  const remoteUsers = useRemoteUsers();

  // Remote names cache
  const [remoteNames, setRemoteNames] = useState<Record<string, string>>({});
  const fetchingNamesRef = useRef<Set<string>>(new Set());
  const ensureRemoteName = async (uidStr: string) => {
    if (!uidStr) return;
    if (remoteNames[uidStr] || fetchingNamesRef.current.has(uidStr)) return;
    fetchingNamesRef.current.add(uidStr);
    try {
      const name = await getUserName(uidStr);
      setRemoteNames((prev) => ({
        ...prev,
        [uidStr]: (name && name.trim()) || "Guest",
      }));
    } catch {
      setRemoteNames((prev) => ({ ...prev, [uidStr]: "Guest" }));
    } finally {
      fetchingNamesRef.current.delete(uidStr);
    }
  };

  // When the list of remote users changes, ensure we resolve their names.
  useEffect(() => {
    remoteUsers.forEach((u) => ensureRemoteName(String(u.uid)));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [remoteUsers.map((u) => u.uid).join("|")]);

  // Permission probe for camera/microphone
  const checkMediaPermissions = async (): Promise<boolean> => {
    if (Platform.OS !== "web") {
      // Native permissions are handled by useNativeMediaPermissions; the OS
      // prompts at capture time (iOS) or via the runtime request (Android).
      return true;
    }
    setIsCheckingPermissions(true);
    setMediaPermissionError(null);
    try {
      if (location.protocol !== "https:" && location.hostname !== "localhost") {
        setMediaPermissionError(
          "HTTPS connection is required for camera and microphone.",
        );
        return false;
      }
      if (!navigator.mediaDevices?.getUserMedia) {
        setMediaPermissionError(
          "Your browser does not support camera and microphone access.",
        );
        return false;
      }
      const probeStream = await navigator.mediaDevices.getUserMedia({
        video: true,
        audio: true,
      });
      probeStream.getTracks().forEach((t) => t.stop());
      return true;
    } catch (error: any) {
      const name: string | undefined = error?.name;
      if (name === "NotAllowedError")
        setMediaPermissionError("Please allow camera and microphone.");
      else if (name === "NotFoundError")
        setMediaPermissionError("Camera or microphone not found.");
      else if (name === "NotSupportedError")
        setMediaPermissionError("Your browser does not support WebRTC video.");
      else
        setMediaPermissionError(
          `Media access error: ${error?.message ?? String(error)}`,
        );
      return false;
    } finally {
      setIsCheckingPermissions(false);
    }
  };

  // Fetch token + start calling
  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        setIsFetchingToken(true);
        const ok = await checkMediaPermissions();
        if (!ok) return;
        const creds = await fetchRoomToken(channelId);
        if (!cancelled) {
          setAgoraCreds(creds);
          setCalling(true); // triggers useJoin
          // Log room join event
          logJoinRoom(channelId, roomName);
          markUserJoinedRoom();
        }
      } catch {
        Alert.alert("Join Error", "Could not get room credentials.");
        navigationReplace("MeetingsTabs");
      } finally {
        if (!cancelled) setIsFetchingToken(false);
      }
    })();
    return () => {
      cancelled = true;
      // Best-effort local cleanup
      setCalling(false);
      setRemoteNames({});
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [channelId]);

  // Room presence -> Firestore
  useEffect(() => {
    if (!isConnected || hasJoinedPresenceRef.current) return;

    hasJoinedPresenceRef.current = true;
    joinRoomPresence(channelId, userName).catch((error) => {
      hasJoinedPresenceRef.current = false;
      console.warn("Failed to join room presence:", error);
    });
  }, [isConnected, channelId, userName]);

  useEffect(() => {
    if (!isConnected || !hasJoinedPresenceRef.current) return;

    const heartbeatId = setInterval(() => {
      heartbeatRoomPresence(channelId).catch((error) => {
        console.warn("Failed to heartbeat room presence:", error);
      });
    }, 30 * 1000);

    return () => clearInterval(heartbeatId);
  }, [isConnected, channelId]);

  useEffect(() => {
    return () => {
      if (!hasJoinedPresenceRef.current) return;

      hasJoinedPresenceRef.current = false;
      leaveRoomPresence(channelId).catch((error) => {
        console.warn("Failed to leave room presence on cleanup:", error);
      });
    };
  }, [channelId]);

  // 5-minute warning modal
  const [isTimeWarningModalVisible, setIsTimeWarningModalVisible] =
    useState(false);
  const [hasShownTimeWarning, setHasShownTimeWarning] = useState(false);
  useEffect(() => {
    if (
      currentTime >= room.alertBeforeEndAt.getTime() &&
      !hasShownTimeWarning
    ) {
      setIsTimeWarningModalVisible(true);
      setHasShownTimeWarning(true);
    }
  }, [currentTime, room.alertBeforeEndAt, hasShownTimeWarning]);

  // Conversation panel
  const [isConversationPanelOpen, setIsConversationPanelOpen] = useState(false);

  const participantsCount = (isConnected ? 1 : 0) + remoteUsers.length;

  const handleToggleMic = async () => {
    setMicOn((prev) => !prev);
  };

  const handleToggleCamera = async () => {
    setCameraOn((prev) => !prev);
  };

  const hasLeftRef = useRef(false);
  const handleLeave = useCallback(() => {
    // The header X and the auto-end timer can both fire; leave only once.
    if (hasLeftRef.current) return;
    hasLeftRef.current = true;
    // Leaving used to wait for the tracks to stop and for the Firestore
    // presence write before navigating, which froze the X button for a few
    // seconds (#142). Navigate first and finish the cleanup in the background.
    setCalling(false); // triggers leave via useJoin
    setRemoteNames({});
    navigationReplace("PostCall");
    // Stop local capture to prevent echo after leave. On native the engine is
    // released when the call screen unmounts, which stops capture already.
    if (Platform.OS === "web") {
      localMicrophoneTrack?.setEnabled(false).catch(() => {});
      localCameraTrack?.setEnabled(false).catch(() => {});
    }
    if (hasJoinedPresenceRef.current) {
      hasJoinedPresenceRef.current = false;
      leaveRoomPresence(channelId).catch((error) => {
        console.warn("Failed to leave room presence:", error);
      });
    }
  }, [channelId, localMicrophoneTrack, localCameraTrack, navigationReplace]);

  // Auto end at endAt
  useEffect(() => {
    if (currentTime >= room.endAt.getTime()) {
      handleLeave();
    }
  }, [currentTime, room.endAt, handleLeave]);

  const handleFlagUser = useCallback(
    (flaggedUid: string, flaggedName: string) => {
      setFlaggedUserId(flaggedUid);
      setFlaggedUserName(flaggedName);
      setIsReportModalVisible(true);
    },
    [],
  );

  const handleReportSubmit = async (reason: string, details: string) => {
    setIsSubmittingReport(true);
    try {
      const flagsRef = collection(db, "flags");
      await addDoc(flagsRef, {
        flaggedUserId: flaggedUserId,
        flaggedUserName: flaggedUserName,
        flaggedByUserId: auth.currentUser?.uid || "unknown",
        flaggedByUserName: userName || "Guest",
        roomId: channelId,
        roomName: roomName,
        reason: reason,
        details: details,
        createdAt: serverTimestamp(),
      });
      setIsReportModalVisible(false);
      if (Platform.OS === "web") {
        window.alert(
          "Thank you. The participant has been flagged for moderation.",
        );
      } else {
        Alert.alert(
          "Reported",
          "Thank you. The participant has been flagged for moderation.",
        );
      }
    } catch (error) {
      console.error("Error submitting report:", error);
      throw error;
    } finally {
      setIsSubmittingReport(false);
    }
  };

  useEffect(() => {
    setCallHeaderLeaveHandler(handleLeave);
    return () => setCallHeaderLeaveHandler(null);
  }, [handleLeave]);

  return (
    <>
      <CallWebView
        roomName={roomName}
        channelLabel={agoraCreds?.channelId ?? channelId}
        participantsCount={participantsCount}
        calling={calling}
        mediaPermissionError={mediaPermissionError}
        isCheckingPermissions={isCheckingPermissions}
        isFetchingToken={isFetchingToken}
        userName={userName}
        remoteUsers={remoteUsers}
        remoteNames={remoteNames}
        localMicrophoneTrack={localMicrophoneTrack}
        localCameraTrack={localCameraTrack}
        micOn={micOn}
        cameraOn={cameraOn}
        isBlurEnabled={isBlurEnabled}
        isConversationPanelOpen={isConversationPanelOpen}
        isTimeWarningModalVisible={isTimeWarningModalVisible}
        onRetryPermissions={checkMediaPermissions}
        onToggleMic={handleToggleMic}
        onToggleCamera={handleToggleCamera}
        onToggleBlur={toggleBlur}
        onToggleConversationPanel={() =>
          setIsConversationPanelOpen((prev) => !prev)
        }
        onCloseConversationPanel={() => setIsConversationPanelOpen(false)}
        onCloseTimeWarning={() => setIsTimeWarningModalVisible(false)}
        onLeave={handleLeave}
        onFlagUser={handleFlagUser}
      />
      <ReportUserModal
        visible={isReportModalVisible}
        flaggedUserName={flaggedUserName}
        onClose={() => setIsReportModalVisible(false)}
        onSubmit={handleReportSubmit}
        isSubmitting={isSubmittingReport}
      />
    </>
  );
}
